// GLOBAL customer-payment surface — the receivable twin of Procurement/HeaderMakePayment, and THE one customer-payment drawer
// (shell 'payment' door; Sales Orders, the order page, Receivables rows and the Data Entry menu all open it, pinned to the order/customer).
// 47002: every figure here is the API's. The order universe is E12 (v_sales_order) via GET /api/sales-orders, whose rows carry the order's
// collectable_cap / returned_value / remaining_to_deliver_value / prepayable_value / customer credit (api lib/customerPosition.js); the customer
// level reads the customer position snapshot on GET /reports/aged-receivables. The browser derives none of them. A payment pinned to an order is
// capped by the API (422 PAYMENT_EXCEEDS_COLLECTABLE) and, on undelivered goods, becomes that order's DEPOSIT (Deferred Revenue); a payment with no
// order is an on-account transfer. Money leg = POST /finance (one idempotency key per attempt); the FIFO/allocation split is the backend's + AllocationEditor's.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import Combobox from '../Combobox';
// Shared door rule (Add Discount / Add Fees / Make Payment): the ORDER selector offers only not-done orders.
import OpenOrderPicker from '../Sales/OpenOrderPicker';
import { DrawerActions } from '../../shell/DrawerBody';
import useOrderPickerLabel from '../Sales/useOrderPickerLabel';
import { useTranslation, Trans } from 'react-i18next';
import { refusalOf } from '../../shell/drawer/refusal.mjs';
import { isOfferable } from '../Sales/salesDoorContext.mjs';
import AllocationEditor from '../Procurement/AllocationEditor';
import { isPickableFunding, systemAccountIdForType } from '../../lib/resolveSupplierAccount';
import { isOrderLessItem } from '../../lib/procurementCalc';
import { positionRows } from '../../lib/customerPosition';
import PaymentRecon from '../sale/PaymentRecon';
import { keyFor, clearKey } from '../../lib/idempotencyKey.mjs';
import { formatMoney, formatMoneyPrecise, formatMoneyExact } from '../../utils/format';
import useActionLabel from '../../i18n/useActionLabel';

import { CloseBtn, Tx } from '../GhostBits';
import './HeaderMakeCustomerPayment.css';
import EnumSelect from '../EnumSelect';
import { AmountInput, TextInput } from '../inputs/Input';
const today = () => new Date().toISOString().slice(0, 10);
const EPS = 0.005;
// GES-406 — separators, decimals and currency label/placement from Settings ▸ Formatting (shared formatters).
const fmt = n => formatMoney(Number(n) || 0);
const fmt2 = n => formatMoneyPrecise(Number(n) || 0);
const isCustomer = p => ['customer', 'client'].includes(String(p?.type || '').toLowerCase());
// The cap of one order is the API's `collectable_cap` (owed on the delivered part + undelivered goods that may be prepaid, never more than the
// customer's net position). No browser-side Ordered - Paid.
const capOf = r => (r?.collectable_cap == null ? null : Math.max(0, Number(r.collectable_cap) || 0));   // null = the API could not read the customer position (never a guessed number)

// E6 (47019, R-B): the customer holds credit the payment could use. Two ways out: apply that credit (the order page's own door), or record the payment anyway (re-sent with payment_warning_ack and a NEW key). Figures are the server's text.
function CreditHoldsNotice({ w, busy, onAnyway, onApply }) {
  const { t } = useTranslation('sales');
  return (
    <div className="hmcp-14" role="alert" data-testid="customer-pay-holds">
      <div>{t('custPay.holds', { credit: formatMoneyExact(w.customer_credit) })}</div>
      <div className="act-row">
        {onApply && <button type="button" className="btn sm" disabled={busy} onClick={onApply} data-testid="customer-pay-apply-credit">{t('custPay.applyCredit')}</button>}
        <button type="button" className="btn sm" disabled={busy} onClick={onAnyway} data-testid="customer-pay-anyway">{t('custPay.recordAnyway')}</button>
      </div>
    </div>
  );
}

export function CustomerPaymentPanel({ customer, receivableAccount, operationId, pinnedKind, recon, capAmount, onClose, onSuccess, title }) {
  const { t: tn, i18n } = useTranslation('sales');
  const al = useActionLabel();
  const [cashAccounts, setCashAccounts] = useState([]);
  const [cashAccountId, setCashAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(today());
  const [notes, setNotes] = useState('');
  const [phase, setPhase] = useState('form');
  const [settlementLegId, setSettlementLegId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [writtenOff, setWrittenOff] = useState(false);   // E4: the order was written off (409 PAYMENT_ON_WRITTEN_OFF): offer 'record on account'
  const [holds, setHolds] = useState(null);   // E6: the 409 PAYMENT_CUSTOMER_HOLDS_CREDIT body
  const submitInFlight = useRef(false);
  const keyHolder = useRef(null);   // one idempotency key per attempt: a retry or double submit of the SAME payment replays instead of double-posting
  const cap = Number(capAmount);
  const hasCap = Number.isFinite(cap) && cap > 0;
  const overCap = Number.isFinite(cap) && Number(amount) > cap + EPS;
  const pinnedToOrder = !!operationId && pinnedKind === 'order';   // an order-pinned payment is hard-capped (the API refuses more); a customer/on-account one is not

  useEffect(() => {
    api.getAccounts().then(rows => setCashAccounts((rows || [])
      .filter(a => isPickableFunding(a, ['cash', 'bank']))
      .map(a => ({ value: a.id, label: a.name }))))
      .catch(() => setCashAccounts([]));
  }, []);

  async function submit(onAccount = false, ack = false) {
    if (submitInFlight.current) return;
    if (!cashAccountId) { setError(tn('custPay.err.cashAcct')); return; }
    if (!(Number(amount) > 0)) { setError(tn('custPay.err.amount')); return; }
    if (!receivableAccount?.id) { setError(tn('custPay.err.noRecv')); return; }
    if (pinnedToOrder && overCap) { setError(tn('customerNet.recon.overCap', { amount: fmt2(Number(amount) - cap) })); return; }
    submitInFlight.current = true;
    setSubmitting(true); setError(null); setWrittenOff(false); setHolds(null);
    try {
      // Customer collection is AR/Deferred -> cash/bank. The backend is the accounting authority and
      // auto-FIFO/deferred-splits; the next phase uses POST /payment-allocations to confirm/adjust.
      const body = {
        type: 'Payment', subtype: 'Customer Payment', partner_id: customer.id,
        amount: Number(amount), account_from_id: receivableAccount.id, account_to_id: cashAccountId,
        operation_id: onAccount ? null : operationId || null, date, notes: notes || null, tx_status: 'posted',
        ...(ack ? { payment_warning_ack: true } : {}),
      };
      const leg = await api.request('/finance', { method: 'POST', body: JSON.stringify({ ...body, idempotency_key: keyFor(keyHolder, JSON.stringify(body)) }) });
      clearKey(keyHolder);
      if (!leg?.id) throw new Error(tn('custPay.err.noRef'));
      window.dispatchEvent(new Event('customer-money-changed'));   // the order page / receivables worklist behind this drawer refresh
      if (leg.kind === 'order_deposit' && !leg.payment_leg) {      // the whole amount became the order's deposit: nothing left to allocate
        onSuccess?.({ deposit: true, deposit_leg: leg.deposit_leg, payment_status: 'paid_in_advance' });
        return;
      }
      setSettlementLegId(leg.settlement_leg_id || leg.id);
      setPhase('allocate');
    } catch (e) {
      if (e && e.body && e.body.code === 'PAYMENT_CUSTOMER_HOLDS_CREDIT') setHolds(e.body);
      else if (e && e.code === 'PAYMENT_ON_WRITTEN_OFF') { setWrittenOff(true); setError(tn('custPay.err.writtenOff')); }
      else setError(e && e.body ? refusalOf(e.body, i18n.language).text || tn('custPay.err.failed') : e && !e.status && e.message ? e.message : tn('custPay.err.failed'));   // a coded refusal reads the server text (error_fr in French); our own local checks keep their translated message
    } finally {
      submitInFlight.current = false;
      setSubmitting(false);
    }
  }

  if (phase === 'allocate') return (
    <div className="hmcp-1">
      <div className="hmcp-2">{tn('custPay.recorded')}</div>
      <div className="hmcp-3">
        {tn('custPay.fifoNote')}
      </div>
      <AllocationEditor settlementLegId={settlementLegId} operationId={operationId || null}
        onSaved={() => onSuccess?.({ settlementLegId, payment_status: 'refreshed' })} />
      <div className="hmcp-4">
        <button className="btn success sm" type="button" onClick={() => onSuccess?.({ settlementLegId, payment_status: 'refreshed' })}
          >Done</button>
      </div>
    </div>
  );

  return (
    <div className="hmcp-1">
      <div className="hmcp-5">{title || tn('custPay.titleFor', { name: customer.name })}</div>
      <PaymentRecon recon={recon} />
      <div className="hmcp-6">
        <div><label className="hmcp-7">{tn('custPay.date')}</label><TextInput type="date" value={date} onChange={e => setDate(e.target.value)} className="hmcp-8" /></div>
        <div>
          <label className="hmcp-7">{tn('custPay.amount')}{hasCap ? ` · ${tn('custPay.max', { amount: fmt2(cap) })}` : ''}</label>
          <AmountInput data-testid="customer-pay-amount" min="0" step="any" value={amount} onChange={e => setAmount(e.target.value)} className={`hmcp-field${overCap ? ' bad' : ''}`} />
          {hasCap && <button type="button" onClick={() => setAmount(String(cap))} className="btn sm">{tn('customerNet.recon.fill', { amount: fmt2(cap) })}</button>}
        </div>
        <div><label className="hmcp-7">{tn('custPay.deposit')}</label><Combobox options={cashAccounts} value={cashAccountId} onChange={setCashAccountId} placeholder={tn('custPay.acctPh')} /></div>
        <div><label className="hmcp-7">{tn('custPay.notes')}</label><textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} className="hmcp-10" /></div>
      </div>
      {overCap && (pinnedToOrder
        ? <div data-testid="customer-pay-overcap" className="hmcp-11">{tn('customerNet.recon.overCap', { amount: fmt2(Number(amount) - cap) })}</div>
        : <div className="hmcp-12">{fmt2(Number(amount) - cap)} above current dues → recorded as on-account credit for this customer (applied oldest-first, the remainder held on account).</div>)}
      {pinnedToOrder && recon && recon.prepayable > EPS && <div data-testid="customer-pay-deposit-note" className="hmcp-13">{tn('customerNet.recon.depositNote')}</div>}
      {holds && <CreditHoldsNotice w={holds} busy={submitting} onAnyway={() => submit(false, true)}
        onApply={operationId && holds.apply_credit ? () => { window.dispatchEvent(new CustomEvent('order-door-request', { detail: { op: operationId, door: 'apply_customer_money' } })); onClose && onClose(); } : null} />}
      {error && <div className="hmcp-14" data-testid="customer-pay-error">{error}</div>}
      {writtenOff && <div className="hmcp-14" data-testid="customer-pay-wo"><button type="button" className="btn sm" disabled={submitting} onClick={() => submit(true)} data-testid="customer-pay-onaccount">{tn('custPay.recordOnAccount')}</button> <span className="sub">{tn('custPay.reverseWoHint')}</span></div>}
      <DrawerActions legacyRow="act-row"
        legacyClose={onClose && <button type="button" onClick={onClose} className="btn">{tn('custPay.cancel')}</button>}>
        {inFoot => (
          <button data-testid="customer-pay-submit" type="button" onClick={() => submit()} disabled={submitting || (pinnedToOrder && (overCap || recon?.unavailable))}
            {...(inFoot ? { className: 'btn success' } : { className: 'btn success sm' })}>
            {submitting ? tn('custPay.processing') : al('recordCustomerPayment')}
          </button>
        )}
      </DrawerActions>
    </div>
  );
}

export default function HeaderMakeCustomerPayment({ open, onClose, onSuccess, partners: suppliedPartners = [], embedded = false, initialPartnerId = '', initialOperationId = '', lockOrder = false, lockPartner = false, onLeave }) {
  const { t: ts } = useTranslation('sales');
  const pickerLabel = useOrderPickerLabel();
  const [accounts, setAccounts] = useState([]);
  const [openOrders, setOpenOrders] = useState([]);
  const [agedRows, setAgedRows] = useState([]);
  const [universeState, setUniverseState] = useState('idle');
  const [loadedPartners, setLoadedPartners] = useState([]);
  const [customerId, setCustomerId] = useState(initialPartnerId);
  const [operationId, setOperationId] = useState(initialOperationId);
  const [successMessage, setSuccessMessage] = useState('');

  // The obligation universe is BOTH engines: E12 open orders (delivered AR + collectable advances the aged
  // engine can't see) AND E11 aged-receivables ORDER-LESS obligations (advances / manual AR / credits with
  // no sales order). Order-less obligations show in the total to-be-received, so they must be payable here too.
  // The load is tracked as ONE universeState so a locked order stays "locked + loading" until BOTH resolve,
  // rather than momentarily falling through to the unlocked/empty state while the async fetch is in flight.
  const reloadUniverse = () => {
    setUniverseState('loading');
    return Promise.all([
      api.request('/sales-orders?page_size=200'),
      api.request('/reports/aged-receivables'),
    ]).then(async ([res, rows]) => {
      let list = Array.isArray(res?.rows) ? res.rows : (Array.isArray(res) ? res : []);
      // a pinned order outside the first page (older/newer than the 200 loaded) is fetched by id, never reported as "no longer open"
      if (initialOperationId && !list.some(r => r.operation_id === initialOperationId)) {
        // 47082 (stranded receivable): the list hides a wholly CANCELLED order unless the Delivery facet asks for it (A7j); a cancelled order that still owes money is pinned too
        const find = async (extra) => { const one = await api.request(`/sales-orders?search=${encodeURIComponent(initialOperationId)}&page_size=5${extra}`).catch(() => null);
          return (Array.isArray(one?.rows) ? one.rows : (Array.isArray(one) ? one : [])).filter(r => r.operation_id === initialOperationId); };
        let hit = await find('');
        if (!hit.length) hit = await find('&delivery_status=cancelled');
        list = [...list, ...hit];
      }
      setOpenOrders(list);
      setAgedRows(Array.isArray(rows) ? rows : []);
      setUniverseState('ready');
    }).catch(() => { setOpenOrders([]); setAgedRows([]); setUniverseState('error'); });
  };
  useEffect(() => {
    if (!open) return;
    api.getAccounts().then(rows => setAccounts(Array.isArray(rows) ? rows : [])).catch(() => setAccounts([]));
    reloadUniverse();
    if (!suppliedPartners.length) api.getPartners().then(rows => setLoadedPartners(Array.isArray(rows) ? rows : [])).catch(() => setLoadedPartners([]));
  // suppliedPartners is intentionally an input snapshot from a drawer/page, not a fetch trigger.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => { if (open) { setCustomerId(initialPartnerId || ''); setOperationId(initialOperationId || ''); } }, [open, initialPartnerId, initialOperationId]);

  const partners = suppliedPartners.length ? suppliedPartners : loadedPartners;
  const customers = useMemo(() => partners.filter(isCustomer), [partners]);
  // UNIFIED obligation universe keyed by operation_id: E12 orders (kind 'order') + E11 order-less obligations
  // (kind 'orderless'). An order-less receivable carries its own operation_id; the on-account pool (no
  // operation_id) is excluded. This is what makes order-less payable here, mirroring the buy side.
  const obligationIndex = useMemo(() => {
    const map = new Map();
    for (const r of openOrders) {
      if (!r.operation_id) continue;
      map.set(r.operation_id, { kind: 'order', operation_id: r.operation_id, partner_id: String(r.partner_id || ''), date: r.op_date || '', collectable: capOf(r) ?? 0, unavailable: capOf(r) == null, e12: r });
    }
    for (const acct of agedRows) for (const it of (acct.items || [])) {
      if (!it.operation_id || acct.account_role || !isOrderLessItem(it, 'receivable')) continue;
      const amt = Number(it.open_amount ?? it.amount) || 0;
      if (amt <= EPS) continue;
      if (map.get(it.operation_id)?.kind === 'order') continue;   // an order already claims this op
      const prev = map.get(it.operation_id);
      map.set(it.operation_id, { kind: 'orderless', operation_id: it.operation_id, partner_id: String(it.partner_id || acct.partner_id || ''), date: it.date || it.due_date || '', collectable: (prev?.collectable || 0) + amt });
    }
    return map;
  }, [openOrders, agedRows]);
  // Done orders are never offered as a TARGET (owner rule; they are handled from the order page). Only the order
  // selector is filtered: order-less obligations, the customer selector, the customer-level allocation (obligationIndex)
  // and the on-account option are untouched, and a locked order still resolves through obligationIndex.
  const obligationOptions = [...obligationIndex.values()]
    .filter(o => o.collectable > EPS && (o.kind !== 'order' || isOfferable(o.e12)))   // the picker offers what can still be collected; a locked order resolves below even at 0
    .filter(o => !customerId || o.partner_id === String(customerId))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  const pinned = operationId ? obligationIndex.get(operationId) : null;
  // An obligation pins its own customer; the customer dropdown drives the customer-scoped (allocate-across) path.
  const effectiveCustomerId = pinned ? pinned.partner_id : customerId;
  const customer = customers.find(p => String(p.id) === String(effectiveCustomerId)) || null;
  const receivableAccountId = customer ? (customer.default_account_id || (customer.is_system ? systemAccountIdForType(accounts, 'client') : null)) : null;
  const receivableAccount = accounts.find(a => a.id === receivableAccountId && String(a.type || '').toLowerCase() === 'client') || null;

  // The reconciliation lays out API fields only: the order row's money (net of returns), its collectable_cap, or the customer position snapshot.
  const reconOf = (o) => {
    if (o.kind === 'order') {
      const r = o.e12;
      return { kind: 'order', ordered: Number(r.ordered_value) || 0, returned: Number(r.returned_value) || 0, netOrder: Number(r.net_order_value) || 0,
        delivered: Number(r.recognized_value ?? r.invoiced_amount) || 0, remaining: Number(r.remaining_to_deliver_value) || 0, deposit: Number(r.deposits_held) || 0,
        prepayable: Number(r.prepayable_value) || 0, ar: Number(r.ar_owed ?? r.amount_owed) || 0, own: Number(r.own_collectable) || 0,
        customerCap: r.customer_collectable_cap == null ? null : Number(r.customer_collectable_cap), collectable: capOf(r) ?? 0, unavailable: capOf(r) == null };
    }
    return { kind: 'orderless', orderless: o.collectable, collectable: o.collectable };   // order-less: a flat amount owed, no delivery/Deferred clock
  };
  const recon = useMemo(() => {
    if (pinned) return { label: pinned.kind === 'orderless' ? ts('custPay.orderless', { id: pinned.operation_id }) : ts('custPay.order', { id: pinned.operation_id }), ...reconOf(pinned) };
    if (effectiveCustomerId) {
      const c = positionRows(agedRows, effectiveCustomerId)[0];
      if (!c) return null;
      return { kind: 'customer', label: ts('custPay.allObl', { name: customer?.name || ts('custPay.customerWord') }), gross: Number(c.gross_receivable) || 0, remaining: Number(c.remaining_to_deliver_value) || 0,
        onAccount: Number(c.credit_on_account) || 0, advances: Number(c.advances_held) || 0, refunds: Number(c.refund_collectable_customer) || 0, collectable: Number(c.collectable_cap) || 0 };
    }
    return null;
  // reconOf is a pure layout helper over the same inputs; safe to omit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinned, effectiveCustomerId, agedRows, customer]);
  const capAmount = recon ? recon.collectable : null;

  if (!open) return null;
  const complete = async result => {
    reloadUniverse();
    // E12 owns the money-clock label. For an order-pinned payment, re-read it rather than infer from the amount.
    let paymentStatus = null;
    if (operationId) {
      try {
        const order = await api.request(`/sales-orders/${encodeURIComponent(operationId)}`);
        paymentStatus = order?.header?.payment_status || null;
      } catch { /* the finance write stands; the worklist callback will still refresh E12 */ }
    }
    setSuccessMessage(result?.deposit ? ts('customerNet.recon.depositRecorded', { op: operationId })
      : paymentStatus ? ts('custPay.e12', { status: String(paymentStatus).replace(/_/g, ' ') }) : ts('customerNet.recon.recorded'));
    onSuccess?.({ ...result, payment_status: paymentStatus || result?.payment_status || 'refreshed' });
  };

  return <div className={embedded ? undefined : 'hmcp-out'}>
    <div className={embedded ? undefined : 'hmcp-card'}>
      <div className={embedded ? 'hmcp-hide' : 'hmcp-bar'}>
        <div className="hmcp-16">{ts('custPay.title')}</div>
        {onClose && <><span className="hmcp-grow" /><CloseBtn onClick={onClose} /></>}
      </div>
      <div className="hmcp-17">
        <div className="hmcp-18"><label className="hmcp-7">{ts('custPay.orders')}{lockOrder && operationId ? ` · ${ts('custPay.locked')}` : ''}</label>{lockOrder && operationId
          ? <div className="hmcp-19" title={operationId}>{operationId}{pinned ? ` · ${pinned.kind === 'orderless' ? 'order-less · ' : ''}${fmt(pinned.collectable)} collectable` : ''}</div>
          : <OpenOrderPicker options={obligationOptions.map(o => ({ value: o.operation_id, label: pickerLabel(o.kind === 'orderless' ? { operation_id: o.operation_id, customer_name: ts('orderPicker.orderless'), op_date: o.date } : o.e12, { extra: ts('orderPicker.collectable', { amount: fmt(o.collectable) }) }) }))} value={operationId} onChange={id => { setOperationId(id); }} placeholder={ts('custPay.anyOrderPh')} style={{ display: 'block', width: '100%' }} loaded={universeState === 'ready'} onLeave={onLeave || onClose} />}</div>
        <div className="hmcp-20"><label className="hmcp-7">{ts('custPay.customer')}{lockPartner && effectiveCustomerId ? ` · ${ts('custPay.locked')}` : ''}</label><EnumSelect bare testid="customer-pay-customer" value={effectiveCustomerId} disabled={!!pinned || (lockPartner && !!effectiveCustomerId)} onChange={v => setCustomerId(v)}><option value="">{ts('custPay.anyCustomer')}</option>{customers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</EnumSelect></div>
      </div>
      <div className="hmcp-21"><Trans t={ts} i18nKey="custPay.pickHint" components={{ b: <strong /> }} /></div>
      {successMessage && <div className="hmcp-22">{successMessage}</div>}
      {universeState === 'loading' || universeState === 'idle' ? <div className="hmcp-23">{ts('custPay.loading')}</div>
        : universeState === 'error' ? <div className="hmcp-24">{ts('custPay.loadErr')} <button type="button" onClick={reloadUniverse} className="btn sm"><Tx k="ghost.retry" /></button></div>
        : lockOrder && operationId && !pinned ? <div className="hmcp-24">{ts('custPay.closed')}</div>
        : customer && !receivableAccount ? <div className="hmcp-25">{ts('custPay.noAcct')}</div>
        : customer && receivableAccount ? <div className="hmcp-26"><CustomerPaymentPanel key={`${customer.id}:${operationId || 'all'}`} customer={customer} receivableAccount={receivableAccount} operationId={operationId || null} pinnedKind={pinned ? pinned.kind : null} recon={recon} capAmount={capAmount} onClose={onClose} onSuccess={complete} title={ts('custPay.titleFor', { name: customer.name })} /></div>
          : <div className="hmcp-27">{ts('custPay.begin')}</div>}
    </div>
  </div>;
}
